import { useRef, useState } from "react";
import {
  balanceSheet,
  format,
  incomeStatement,
  negate,
  trialBalance,
} from "@core/index";
import {
  downloadCsv,
  entriesToCsv,
  previewImport,
  rowsToCsv,
  trialBalanceToCsv,
  type ImportPreview,
} from "@data/index";
import { useBrand } from "@branding/useBrand";
import { useLedger } from "@ui/useLedger";

type Report =
  | "tb-consolidated"
  | "tb-entity"
  | "pnl"
  | "bs"
  | "journal";

export function ImportExport() {
  const { accounts, entries, entities, base, entityId, scopeName } = useLedger();
  const brand = useBrand();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [report, setReport] = useState<Report>("tb-consolidated");

  async function handleFile(file: File) {
    const text = await file.text();
    setFileName(file.name);
    setPreview(previewImport(text));
  }

  function buildExport(): { csv: string; name: string } {
    const locale = brand.locale;
    switch (report) {
      case "tb-entity": {
        const tb = trialBalance(entries, accounts, base, scopeName, entityId);
        return { csv: trialBalanceToCsv(tb, locale), name: `trial-balance-${entityId ?? "scope"}.csv` };
      }
      case "pnl": {
        const p = incomeStatement(entries, accounts, base, entityId);
        const rows: string[][] = [
          ["Revenue", format(p.revenue, locale, { parenNegative: false })],
          ["Cost of sales", format(negate(p.costOfSales), locale, { parenNegative: false })],
          ["Gross profit", format(p.grossProfit, locale, { parenNegative: false })],
          ["Operating expenses", format(negate(p.operatingExpenses), locale, { parenNegative: false })],
          ["FX gain/(loss)", format(p.fxResult, locale, { parenNegative: false })],
          ["Profit before tax", format(p.profitBeforeTax, locale, { parenNegative: false })],
          ["Corporate tax", format(negate(p.corporateTax), locale, { parenNegative: false })],
          ["Net profit", format(p.netProfit, locale, { parenNegative: false })],
        ];
        return { csv: rowsToCsv(["Line", `Amount (${base})`], rows), name: "profit-and-loss.csv" };
      }
      case "bs": {
        const b = balanceSheet(entries, accounts, base, entityId);
        const rows: string[][] = [
          ...b.assets.map((l) => [l.label, format(l.amount, locale, { parenNegative: false })]),
          ["Total assets", format(b.totalAssets, locale, { parenNegative: false })],
          ...b.liabilities.map((l) => [l.label, format(l.amount, locale, { parenNegative: false })]),
          ...b.equity.map((l) => [l.label, format(l.amount, locale, { parenNegative: false })]),
          ["Total L + E", format(b.totalLiabilitiesEquity, locale, { parenNegative: false })],
        ];
        return { csv: rowsToCsv(["Line", `Amount (${base})`], rows), name: "balance-sheet.csv" };
      }
      case "journal":
        return { csv: entriesToCsv(entries, locale), name: "journal.csv" };
      case "tb-consolidated":
      default: {
        const tb = trialBalance(entries, accounts, base, "Consolidated group");
        return { csv: trialBalanceToCsv(tb, locale), name: "trial-balance-consolidated.csv" };
      }
    }
  }

  function doExport() {
    const { csv, name } = buildExport();
    downloadCsv(name, csv);
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Import / Export</h1>
          <div className="sub">
            Coexist with your current tool — bring balances in, push statements out. No forced
            migration.
          </div>
        </div>
      </div>

      <div className="grid two">
        <div className="card">
          <div className="card-head">
            <h2>Import CSV</h2>
          </div>
          <div className="card-body">
            <div className="steps">
              <div className={`step${!preview ? " active" : ""}`}>
                <span className="n">1</span> Upload
              </div>
              <div className={`step${preview ? " active" : ""}`}>
                <span className="n">2</span> Preview
              </div>
              <div className="step">
                <span className="n">3</span> Map &amp; post
              </div>
            </div>

            {!preview ? (
              <div
                className={`dropzone${dragging ? " drag" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  const file = e.dataTransfer.files[0];
                  if (file) void handleFile(file);
                }}
              >
                <div className="glyph" style={{ fontSize: 26 }} aria-hidden="true">
                  ⇪
                </div>
                <p className="mt3">
                  <strong>Drop a CSV here</strong> or choose a file
                </p>
                <p className="tiny">Trial balance, journal, or invoice exports · UTF-8</p>
                <button
                  type="button"
                  className="btn secondary mt4"
                  onClick={() => fileInput.current?.click()}
                >
                  Choose file
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".csv,text/csv"
                  className="visually-hidden"
                  aria-label="Choose CSV file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleFile(file);
                  }}
                />
              </div>
            ) : (
              <div>
                <div className="row between">
                  <span className="badge good">
                    <span className="dot" /> Parsed {preview.rowCount} rows
                  </span>
                  <button
                    type="button"
                    className="btn secondary sm"
                    onClick={() => {
                      setPreview(null);
                      setFileName(null);
                    }}
                  >
                    Choose another
                  </button>
                </div>
                <p className="hint mt3">
                  {fileName} — showing the first rows. Map columns to journal lines in the next step
                  (amounts import as integer minor units, never rounded floats).
                </p>
                <div className="table-wrap mt3">
                  <table className="data">
                    <thead>
                      <tr>
                        {preview.headers.map((h, i) => (
                          <th key={i}>{h || `Col ${i + 1}`}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.slice(0, 6).map((r, i) => (
                        <tr key={i}>
                          {preview.headers.map((_, j) => (
                            <td key={j}>{r[j] ?? ""}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Export</h2>
          </div>
          <div className="card-body">
            <div className="field mt3">
              <label htmlFor="expReport">Report</label>
              <select
                id="expReport"
                className="input"
                value={report}
                onChange={(e) => setReport(e.target.value as Report)}
              >
                <option value="tb-consolidated">Trial balance (consolidated)</option>
                <option value="tb-entity">Trial balance ({scopeName})</option>
                <option value="pnl">Profit &amp; loss</option>
                <option value="bs">Balance sheet</option>
                <option value="journal">Journal (full)</option>
              </select>
            </div>
            <div className="row mt4" style={{ gap: "var(--s4)" }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="expFrom">From</label>
                <input id="expFrom" className="input" type="date" defaultValue="2026-01-01" />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="expTo">To</label>
                <input id="expTo" className="input" type="date" defaultValue="2026-07-21" />
              </div>
            </div>
            <div className="row mt4">
              <button type="button" className="btn primary" onClick={doExport}>
                Export CSV
              </button>
            </div>
            <p className="hint mt4">
              Exports are read-only snapshots — safe to hand to an auditor or bank without granting
              access. {entities.length} entities in this group.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
